import { SparklesIcon } from "lucide-react";
import { memo, useEffect, useRef, useState } from "react";

import { parseClarificationMessage } from "@/feature/ai-planner/lib/chat-message.utils";
import type {
  AiChatAction,
  AiSuggestedQuery,
} from "@/feature/ai-planner/types/ai-chat.types";
import { cn } from "@/lib/utils";
import { ChatResultContent } from "./chat-result-content";
import { ClarificationSuggestions } from "./clarification-suggestions";
import type { ChatMessage } from "../types/chat-ui.types";
import { ClarificationOptions } from "./clarification-options";
import { HighlightedText } from "./highlighted-text";

export const ChatMessageItem = memo(function ChatMessageItem({
  message,
  onAction,
  onSuggestedQuery,
  onStreamingComplete,
  showSuggestions = true,
  actionsDisabled = false,
  pendingSuggestedQueryId,
}: {
  message: ChatMessage;
  onAction?: (action: AiChatAction) => void;
  onSuggestedQuery?: (query: AiSuggestedQuery) => void;
  onStreamingComplete?: (message: ChatMessage) => void;
  showSuggestions?: boolean;
  actionsDisabled?: boolean;
  pendingSuggestedQueryId?: string | null;
}) {
  const isUser = message.role === "user";
  const shouldStreamText =
    !isUser &&
    !!message.text &&
    message.type !== "ERROR" &&
    message.animateText !== false;
  const completedMessageIdRef = useRef<string | null>(null);
  const [visibleTextLength, setVisibleTextLength] = useState(() =>
    shouldStreamText ? 0 : message.text.length,
  );

  useEffect(() => {
    if (!shouldStreamText) {
      return;
    }
    const step =
      message.text.length > 260 ? 6 : message.text.length > 140 ? 4 : 2;

    const timer = window.setInterval(() => {
      setVisibleTextLength((previous) => {
        if (previous >= message.text.length) {
          window.clearInterval(timer);
          return previous;
        }
        return Math.min(message.text.length, previous + step);
      });
    }, 18);

    return () => window.clearInterval(timer);
  }, [message.text.length, shouldStreamText]);

  const visibleText = shouldStreamText
    ? message.text.slice(0, visibleTextLength)
    : message.text;
  const isTextStreaming =
    shouldStreamText && visibleTextLength < message.text.length;
  const parsedClarificationMessage =
    !isUser && !isTextStreaming && message.type === "NEED_MORE_INFO"
      ? parseClarificationMessage(message.text)
      : null;
  const displayText = parsedClarificationMessage?.body ?? visibleText;

  useEffect(() => {
    if (isUser) {
      return;
    }

    const hasCompletedStreaming = shouldStreamText ? !isTextStreaming : true;
    if (!hasCompletedStreaming) {
      return;
    }

    if (completedMessageIdRef.current === message.id) {
      return;
    }

    completedMessageIdRef.current = message.id;
    onStreamingComplete?.(message);
  }, [isTextStreaming, isUser, message, onStreamingComplete, shouldStreamText]);

  if (isUser) {
    return (
      <div className="flex w-full min-w-0 justify-end">
        <div
          className={cn(
            "w-fit max-w-[82%] min-w-0 rounded-3xl bg-muted px-4 py-2.5 text-[15px] leading-relaxed text-foreground sm:max-w-[76%]",
          )}
        >
          <p className="whitespace-pre-wrap break-words">{visibleText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 items-start gap-3">
      <span className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full border border-border/70 bg-muted/60 text-muted-foreground">
        <SparklesIcon className="size-3.5" />
      </span>
      <div className="min-w-0 flex-1 py-0.5 text-[15px] leading-relaxed">
        <HighlightedText
          text={displayText}
          phrases={message.highlightPhrases}
        />
        {parsedClarificationMessage && showSuggestions ? (
          <ClarificationOptions options={parsedClarificationMessage.options} />
        ) : null}
        {!isTextStreaming && showSuggestions ? (
          <ClarificationSuggestions
            clarification={message.clarification}
            onSelect={onSuggestedQuery}
            disabled={actionsDisabled}
            pendingQueryId={pendingSuggestedQueryId}
          />
        ) : null}
        {isTextStreaming ? (
          <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <span>AI is writing</span>
            <span className="inline-flex size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:0ms]" />
            <span className="inline-flex size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:150ms]" />
            <span className="inline-flex size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:300ms]" />
          </div>
        ) : null}

        <ChatResultContent
          message={message}
          onAction={onAction}
          actionsDisabled={actionsDisabled}
          hideContent={isTextStreaming}
        />
      </div>
    </div>
  );
});
